import type { RootStore } from '@/store'
import * as Sentry from '@sentry/react-native'
import * as Crypto from 'expo-crypto'
import type { ParsedToken, User } from 'firebase/auth'
import type { StateCreator } from 'zustand'

export interface AuthenticationStore {
	authToken: string
	authUserId?: string
	authClaims?: ParsedToken
	anonymousId?: string
	loginEmail?: string
	loginState: AuthLoginState
	setLoginEmail: (email: string) => void
	setLoginState: (state: AuthLoginState) => void
	setAuthUserId: (value: string) => void
	signOut: () => void
	authFromFirebaseUser: (token: string, user: User, claims: ParsedToken) => void
}

export enum AuthLoginState {
	PENDING = 1,
	SUCCESSFUL = 2,
	FAILED = 3,
	NO_ACCOUNT = 4,
	LINK_EXPIRED = 5,
	LOGGING_IN = 6,
	CODE_SENT = 7,
}

export const createAuthenticationSlice: StateCreator<
	RootStore,
	[],
	[],
	AuthenticationStore
> = (set) => ({
	authToken: '',
	authUserId: '',
	loginEmail: '',
	anonymousId: Crypto.randomUUID(),
	loginState: AuthLoginState.PENDING,
	setLoginEmail: (email) =>
		set({
			loginEmail: email,
		}),
	setLoginState: (state) =>
		set({
			loginState: state,
		}),
	setAuthUserId: (value) => set({ authUserId: value }),
	signOut: () => {
		set({
			authUserId: '',
			authToken: '',
			loginState: AuthLoginState.PENDING,
			authClaims: undefined,
		})
	},
	authFromFirebaseUser: (token: string, user: User, claims: ParsedToken) => {
		set({
			authToken: token,
			authUserId: user.uid,
			loginEmail: user.email!,
			loginState: AuthLoginState.SUCCESSFUL,
			authClaims: claims,
		})

		Sentry.setUser({ email: user.email!, id: user.uid })
	},
})

export const authenticationStoreSelector = (state: RootStore) => ({
	authToken: state.authToken,
	authUserId: state.authUserId,
	anonymousId: state.anonymousId,
	loginEmail: state.loginEmail,
	loginState: state.loginState,
	setLoginState: state.setLoginState,
	setAuthUserId: state.setAuthUserId,
	signOut: state.signOut,
	isAuthenticated: isAuthenticated(state),
	canDebug: canDebug(state),
	authFromFirebaseUser: state.authFromFirebaseUser,
})

export const isAuthenticated = (state: RootStore) =>
	!!state.authToken && !!state.authUserId

export const canDebug = (state: RootStore) => !!state.authClaims?.['wally_patient_admin']
